import { useFormat } from '@/composables/format';
import type { SelfSellInModel } from '@onrunning/backstage-models';
import { LocalizedString } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useDraftsInitInfoStore } from '../../../stores/drafts-init-info';
import { useUserStore } from '../../../stores/user';
import { SelfSellInService } from '../services/self-sell-in.service';

// Matches any object with the same locale keys as LocalizedString (e.g. 'en-US', 'de', 'fr'…).
// Using Record<keyof LocalizedString, …> keeps the check in sync with the class definition
// and avoids a Volar IDE quirk where `T extends LocalizedString` (class reference) resolves incorrectly.
type IsLocalizedString<T> = T extends Record<keyof LocalizedString, string> ? true : false;

// Recursively replaces every LocalizedString in a type tree with plain `string`.
type ResolveLocalized<T> = T extends undefined
  ? undefined // preserve undefined (handles optional LocalizedString fields)
  : IsLocalizedString<T> extends true
    ? string // LocalizedString → string
    : T extends (infer U)[]
      ? ResolveLocalized<U>[] // array  → recurse into elements
      : T extends object
        ? { [K in keyof T]: ResolveLocalized<T[K]> } // object → recurse into props
        : T; // primitive → keep as-is

export type ResolvedSelfSellInModel = ResolveLocalized<SelfSellInModel>;

// Runtime mirror of IsLocalizedString: API responses are plain JSON, never class instances,
// so we detect LocalizedString by checking that the object is non-empty and every one of its
// keys is a known locale key (Contentful only includes locales that have values, so we can't
// require all keys to be present — only that no non-locale keys exist).
const LOCALIZED_STRING_KEYS = Object.keys(new LocalizedString()) as (keyof LocalizedString)[];
function isLocalizedStringValue(value: object): value is LocalizedString {
  const keys = Object.keys(value);

  return keys.length > 0 && keys.every((key) => LOCALIZED_STRING_KEYS.includes(key as keyof LocalizedString));
}

const selfSellInService = new SelfSellInService();

export function useSelfSellIn() {
  const { getTranslationFromTranslateField } = useFormat();
  const { getInitialInfoData } = useDraftsInitInfoStore();
  const { currentSeason } = storeToRefs(useDraftsInitInfoStore());
  const { selectedAccountId } = storeToRefs(useUserStore());
  const rawSellIn = ref<SelfSellInModel | null>(null);
  const isLoading = ref(true);

  function resolveLocalized(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(resolveLocalized);
    }
    if (value !== null && typeof value === 'object') {
      if (isLocalizedStringValue(value)) {
        return getTranslationFromTranslateField(value);
      }
      const result: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        result[k] = resolveLocalized(v);
      }

      return result;
    }

    return value;
  }

  const sellIn = computed<ResolvedSelfSellInModel | null>(() =>
    rawSellIn.value ? (resolveLocalized(rawSellIn.value) as ResolvedSelfSellInModel) : null,
  );

  onMounted(() => {
    getInitialInfoData(selectedAccountId.value);
  });

  watch(
    currentSeason,
    async (season) => {
      if (season) {
        isLoading.value = true;
        try {
          rawSellIn.value = await selfSellInService.getSellIn(selectedAccountId.value, season);
        } finally {
          isLoading.value = false;
        }
      }
    },
    { immediate: true },
  );

  return { sellIn, isLoading };
}
